import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n';
import { REGISTRATIONS_ENDPOINT } from './constants';
import {
  getRegistrationDetailsQueryKey,
  getRegistrationExpirationsQueryKey,
  getRegistrationListQueryKey,
  getRegistrationListsQueryKey,
  getRegistrationSummaryQueryKey,
  isInRegistrationScope,
  toRegistrationListRequest,
  toRegistrationScopeRequest,
} from './helpers/registrationQueries';
import { useRegistrationScope } from './hooks/useRegistrationScope';
import type {
  TRegistrationDetails,
  TRegistrationExpirations,
  TRegistrationList,
  TRegistrationListParams,
  TRegistrationScopeFilters,
  TRegistrationSummary,
  TRegistrationUpdate,
} from './types';

export const useRegistrationExpirations = ({ countryIds, productIds }: TRegistrationScopeFilters) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const scope = useRegistrationScope();
  const params = toRegistrationScopeRequest({ countryIds, productIds });

  const { data, isError, error } = useQuery({
    queryKey: [...getRegistrationExpirationsQueryKey(scope), params],
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<TRegistrationExpirations>(`${REGISTRATIONS_ENDPOINT}/expirations`, {
        params,
        signal,
      });
      return res.data;
    },
    retry,
  });

  useHandleQueryError(isError, error, 'Failed to load upcoming expirations!', 'Loading upcoming expirations failed!');

  return { data, error };
};

export const useRegistrationSummary = () => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const scope = useRegistrationScope();

  const { data, isError, error } = useQuery({
    queryKey: getRegistrationSummaryQueryKey(scope),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<TRegistrationSummary>(`${REGISTRATIONS_ENDPOINT}/summary`, { signal });
      return res.data;
    },
    retry,
  });

  useHandleQueryError(
    isError,
    error,
    `Failed to load ${TERMINOLOGY.registrationSingle.lower} summary!`,
    `Loading ${TERMINOLOGY.registrationSingle.lower} summary failed!`
  );

  return { data, error };
};

export const useRegistrations = (params: TRegistrationListParams) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const scope = useRegistrationScope();

  const fetchFn = async (signal: AbortSignal): Promise<TRegistrationList> => {
    const res = await apiClient.get<TRegistrationList>(REGISTRATIONS_ENDPOINT, {
      params: toRegistrationListRequest(params),
      signal,
    });
    return res.data;
  };

  const { data, isFetching, isPlaceholderData, isError, error } = useQuery({
    queryKey: getRegistrationListQueryKey(scope, params),
    queryFn: ({ signal }) => fetchFn(signal),
    retry,
    // Keep the previous page visible while filters change, but never another organization's or user's rows.
    placeholderData: (previousData, previousQuery) =>
      previousQuery && isInRegistrationScope(previousQuery.queryKey, scope) ? previousData : undefined,
  });

  useHandleQueryError(
    isError,
    error,
    `Failed to load ${TERMINOLOGY.registrationSingle.lower} list!`,
    `Loading ${TERMINOLOGY.registrationFull.lower} failed!`
  );

  return { data, isFetching, isPlaceholderData, error };
};

export const useRegistrationDetails = (folderId: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const scope = useRegistrationScope();

  const fetchFn = async (signal: AbortSignal): Promise<TRegistrationDetails | null> => {
    const res = await apiClient.get<TRegistrationDetails | ''>(`${REGISTRATIONS_ENDPOINT}/folder/${folderId}`, {
      signal,
    });
    // A legacy registration folder has no details: the backend answers with an empty HTTP 200 body.
    return res.data || null;
  };

  const { data, isError, error } = useQuery({
    queryKey: getRegistrationDetailsQueryKey(scope, folderId),
    queryFn: ({ signal }) => fetchFn(signal),
    retry,
  });

  useHandleQueryError(
    isError,
    error,
    `Failed to load ${TERMINOLOGY.registrationSingle.lower} details for folder ${folderId}!`,
    `Loading ${TERMINOLOGY.registrationSingle.lower} details failed!`
  );

  return { data, error };
};

export const useUpdateRegistration = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const scope = useRegistrationScope();

  const mutationFn = async (variables: {
    folderId: string;
    update: TRegistrationUpdate;
  }): Promise<TRegistrationDetails> => {
    const res = await apiClient.put(`${REGISTRATIONS_ENDPOINT}/folder/${variables.folderId}`, variables.update);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving ${TERMINOLOGY.registrationSingle.lower} details failed!`, true),
    onSuccess: async (details, { folderId }) => {
      const queryKey = getRegistrationDetailsQueryKey(scope, folderId);
      // An older background read must not overwrite the saved response.
      await queryClient.cancelQueries({ queryKey, exact: true });
      show('success', `${TERMINOLOGY.registrationSingle.upper} details saved.`);
      // Replace, never merge: the response omits cleared dates.
      queryClient.setQueryData(queryKey, details);
      // Status and date changes can move rows between filters and pages.
      queryClient.invalidateQueries({ queryKey: getRegistrationListsQueryKey(scope) });
      queryClient.invalidateQueries({ queryKey: getRegistrationExpirationsQueryKey(scope) });
      queryClient.invalidateQueries({ queryKey: getRegistrationSummaryQueryKey(scope) });
    },
  });
};
